import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Holdings } from './pages/Holdings';
import { Orders } from './pages/Orders';
import { EquityCurve } from './pages/EquityCurve';
import { SettingsPage } from './pages/SettingsPage';
import { Auth } from './pages/Auth';
import { Preview } from './pages/Preview';
import { DeployedStrategies } from './pages/DeployedStrategies';
import { LogViewer } from './pages/LogViewer';
import { CacheAdmin } from './pages/CacheAdmin';
import { SchedulerAdmin } from './pages/SchedulerAdmin';
import { PerformanceAdmin } from './pages/PerformanceAdmin';
import { RegimeScores } from './pages/RegimeScores';
import { RegimeMultiplierAdmin } from './pages/RegimeMultiplierAdmin';

// Placeholder for Admin Invitations page (to be created)
function AdminInvitations() {
  return <div>Admin Invitations - Coming Soon</div>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/invite" element={<Login />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Overview />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/holdings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Holdings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preview"
            element={
              <ProtectedRoute>
                <Layout>
                  <Preview />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* SDO-3: `Strategies` became `DeployedStrategies`. The path is deliberately unchanged —
              renaming it would break every existing bookmark to buy nothing, since what moved is
              ownership of the page's contents, not its address. */}
          <Route
            path="/strategies"
            element={
              <ProtectedRoute>
                <Layout>
                  <DeployedStrategies />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* PB-1: `/backtest`, `/attribution` and `/allocation-analysis` used to be routed
              here. They are Lab-owned research pages and are now reachable only from the Lab
              entry (`src/lab/LabApp.tsx`), served by the Lab process at VITE_LAB_ORIGIN.

              They are *removed*, not hidden: leaving the routes defined while dropping the nav
              links would keep the pages publicly reachable by URL, calling Lab-owned APIs from
              the Trade origin — which is the boundary this milestone exists to draw. The
              catch-all below sends an old bookmark to the dashboard rather than rendering a
              research page, and `Layout` carries an "Open PortfoliYo Lab" link instead.
              Pinned by backend/tests/test_trade_frontend_product_boundary_pb1.py. */}
          <Route
            path="/regime-scores"
            element={
              <ProtectedRoute>
                <Layout>
                  <RegimeScores />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <Orders />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/equity"
            element={
              <ProtectedRoute>
                <Layout>
                  <EquityCurve />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth"
            element={
              <ProtectedRoute>
                <Layout>
                  <Auth />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/invitations"
            element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <AdminInvitations />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/logs"
            element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <LogViewer />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cache"
            element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <CacheAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/scheduler"
            element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <SchedulerAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/performance"
            element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <PerformanceAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/regime-position-multipliers"
            element={
              <ProtectedRoute requireAdmin>
                <Layout>
                  <RegimeMultiplierAdmin />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
